import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  InboxOutlined,
} from '@ant-design/icons'
import { PageContainer } from '@ant-design/pro-layout'
import { makeStyles } from '@material-ui/styles'
import { Button, Col, Divider, Form, Input, Modal, Row, Typography as Text, message } from 'antd'
import moment from 'moment'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect } from 'react-router-dom'
import ReportCard from 'src/components/ReportCard'
import dimorderApi from 'src/libs/api/dimorderApi'
import { PAYMENT_KEYS, PAYMENT_PRESETS } from 'src/libs/payment'
import { isRegion, REGIONS } from 'src/libs/region'
import { actions, useDispatch, useSelector } from 'src/redux'

import colors from '@/style/colors'

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
  },
  text: {
    fontSize: 16,
  },
  boldText: {
    fontSize: 15,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  btn: {
    backgroundColor: colors.button,
    borderColor: colors.button,
    borderRadius: 5,
    color: colors.white,
    width: '100%',
    fontSize: 15,
    height: 35,
  },
})

export default function LinePayAccount(props) {
  const { t } = useTranslation()
  const classes = useStyles(props)
  const dispatch = useDispatch()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const isTaiwanMerchant = isRegion(activeMerchant?.region, REGIONS.TW)
  const [linePayConfig, setLinePayConfig] = useState(null)
  const [isAdding, setIsAdding] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [form] = Form.useForm()

  const linePayPaymentPreset = PAYMENT_PRESETS.LINE_PAY_OFFLINE

  useEffect(() => {
    if (!activeMerchant || !isTaiwanMerchant) return

    const payments = activeMerchant?.payments ?? []
    const linepay = payments.find((payment) => payment.key === PAYMENT_KEYS.LINE_PAY_OFFLINE)
    setLinePayConfig(linepay || null)
  }, [activeMerchant, isTaiwanMerchant])

  if (activeMerchant && !isTaiwanMerchant) {
    return <Redirect to='/' />
  }

  const handleSubmit = async (values) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      const nextLinePayConfig = {
        ...linePayConfig,
        ...linePayPaymentPreset,
        channelId: values.channelId,
        channelSecret: values.channelSecret,
      }
      await dimorderApi.instance(activeMerchant.id)?.updateMerchantPayment(nextLinePayConfig)
      const nextPayments = [
        ...(activeMerchant?.payments ?? []).filter(
          (payment) => payment.key !== PAYMENT_KEYS.LINE_PAY_OFFLINE,
        ),
        nextLinePayConfig,
      ]
      dispatch(actions.merchant.updateMerchantById(activeMerchant.id, { payments: nextPayments }))
      message.success(t('page.linePayAccount.submitSuccess'))
      setIsAdding(false)
      form.resetFields()
    } catch (error) {
      Modal.error({
        title: t('app.common.error'),
        content: t('app.common.submitError'),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = () => {
    Modal.confirm({
      title: t('page.linePayAccount.alert.delete'),
      icon: <ExclamationCircleFilled />,
      okText: t('app.common.yes'),
      okType: 'danger',
      cancelText: t('app.common.no'),
      async onOk() {
        try {
          await dimorderApi.instance(activeMerchant.id)?.updateMerchantPayment({
            ...linePayPaymentPreset,
            remove: true,
          })
          const nextPayments = (activeMerchant?.payments ?? []).filter(
            (payment) => payment.key !== PAYMENT_KEYS.LINE_PAY_OFFLINE,
          )
          dispatch(
            actions.merchant.updateMerchantById(activeMerchant.id, { payments: nextPayments }),
          )
          message.success(t('page.linePayAccount.deleteSuccess'))
          setLinePayConfig(null)
          setIsAdding(false)
        } catch (error) {
          Modal.error({
            title: t('app.common.error'),
            content: t('app.common.submitError'),
          })
        }
      },
    })
  }

  function LinePayListItem() {
    return (
      <Row style={{ paddingBottom: '10px' }}>
        <Col sm={0} xs={9}>
          <Text className={classes.text}>{t('page.linePayAccount.channelId')}</Text>
        </Col>
        <Col sm={8} xs={14}>
          <Text className={classes.text}>{linePayConfig.channelId}</Text>
        </Col>
        <Col sm={0} xs={9}>
          <Text className={classes.text}>{t('page.linePayAccount.channelSecret')}</Text>
        </Col>
        <Col sm={8} xs={14}>
          <Text className={classes.text}>xxxxxxxxxxxx</Text>
        </Col>
        <Col sm={0} xs={9}>
          <Text className={classes.text}>{t('page.linePayAccount.dateAdded')}</Text>
        </Col>
        <Col sm={6} xs={14}>
          <Text className={classes.text}>
            {linePayConfig.createdAt ? moment(linePayConfig.createdAt).format('DD-MM-YYYY') : '-'}
          </Text>
        </Col>
        <Col sm={2} xs={1}>
          <EditOutlined
            style={{ fontSize: 18, color: colors.button, marginRight: 12, cursor: 'pointer' }}
            onClick={() => setIsAdding(true)}
          />
          <DeleteOutlined
            style={{ fontSize: 18, color: colors.error, cursor: 'pointer' }}
            onClick={handleDelete}
          />
        </Col>
      </Row>
    )
  }

  function EmptyState() {
    return (
      <Row style={{ justifyContent: 'center', padding: '25px' }}>
        <Col
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <InboxOutlined style={{ fontSize: '55px' }} />
          <Text className={classes.boldText}>{t('page.linePayAccount.noAccount')}</Text>
        </Col>
      </Row>
    )
  }

  function AddLinePayForm() {
    return (
      <Form
        form={form}
        name='linePayForm'
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        onFinish={handleSubmit}
        autoComplete='off'
      >
        <Form.Item
          label={`${t('page.linePayAccount.channelId')}:`}
          name='channelId'
          rules={[{ required: true, message: t('page.linePayAccount.channelIdRequired') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={`${t('page.linePayAccount.channelSecret')}:`}
          name='channelSecret'
          rules={[{ required: true, message: t('page.linePayAccount.channelSecretRequired') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item className={classes.container}>
          <Button
            type='primary'
            htmlType='submit'
            className={classes.btn}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t('app.common.submit')}
          </Button>
        </Form.Item>
      </Form>
    )
  }

  function AddLinePayButton() {
    if (!isAdding) {
      return (
        <Button
          type='primary'
          className={classes.btn}
          onClick={() => {
            setIsAdding(true)
          }}
        >
          {t('page.linePayAccount.addAccount')}
        </Button>
      )
    }
    return <AddLinePayForm />
  }

  return (
    <PageContainer
      ghost
      header={{
        title: t('page.linePayAccount.title'),
      }}
    >
      <Row className={classes.container}>
        <Col xs={24} xl={14}>
          <ReportCard>
            <Row>
              <Col sm={8} xs={0}>
                <Text className={classes.text}>{t('page.linePayAccount.channelId')}</Text>
              </Col>
              <Col sm={8} xs={0}>
                <Text className={classes.text}>{t('page.linePayAccount.channelSecret')}</Text>
              </Col>
              <Col sm={8} xs={0}>
                <Text className={classes.text}>{t('page.linePayAccount.dateAdded')}</Text>
              </Col>
              <Col sm={24} xs={0}>
                <Divider style={{ borderColor: colors.black, borderWidth: 2 }} />
              </Col>
            </Row>
            {linePayConfig ? <LinePayListItem /> : <EmptyState />}
            {!linePayConfig && (
              <>
                <Divider style={{ borderColor: colors.textTertiary, borderWidth: 1 }} />
                <AddLinePayButton />
              </>
            )}
            {linePayConfig && isAdding && <AddLinePayForm />}
          </ReportCard>
        </Col>
      </Row>
    </PageContainer>
  )
}
